import { useMemo } from 'react';
import { INTL_LOCALES, useT } from '../../../i18n';
import type {
  PoolAccountSummary,
  RegistrationKind,
  RegistrationStatus,
  StandAccountRow,
} from '../testrun-types';
import type { ImportRow, RegistrationImportRow } from './api';

/**
 * What the accounts pages compute from TestRun's answers: phone and IIN input,
 * the payer account, import parsing, money. Ported from TestRun AccountsPage.
 */

/**
 * A phone in any shape → +7XXXXXXXXXX: with or without «+», through 8, ten
 * digits, spaces and brackets. Anything else stays as typed — TestRun says
 * what is wrong with it.
 */
export const normalizePhone = (raw: string): string => {
  const digits = raw.replace(/\D/g, '');
  if (/^[78]\d{10}$/.test(digits)) return `+7${digits.slice(1)}`;
  if (/^\d{10}$/.test(digits)) return `+7${digits}`;
  return raw.trim();
};

export const onlyDigits = (value: string): string => value.replace(/\D/g, '');

/** An IIN/BIN from a spreadsheet cell: digits only; Excel drops leading zeros — put them back up to 12. */
export const digits12 = (cell: string): string => {
  const d = onlyDigits(cell);
  return d.length >= 10 && d.length < 12 ? d.padStart(12, '0') : d;
};

/** A positive amount typed with spaces and a comma, or null. */
export const parseSum = (value: string): number | null => {
  const n = Number(value.replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** An account a payment can use: has an id, open, not blocked, not a special account. */
export const isUsable = (a: StandAccountRow): a is StandAccountRow & { id: string } =>
  Boolean(a.id) && !a.blocked && !a.special && (!a.status || a.status === 'OPENED');

/** The payer account, as TestRun's server picks it: the main one if usable, else the first open tenge one. */
export const payerOf = (accounts: StandAccountRow[]): (StandAccountRow & { id: string }) | null => {
  const main = accounts.find((a) => a.main);
  if (main && isUsable(main) && main.currency === 'KZT') return main;
  return (
    accounts.find(
      (a): a is StandAccountRow & { id: string } => isUsable(a) && a.currency === 'KZT',
    ) ?? null
  );
};

/**
 * Whether the account's list of accounts does not load (as accountsListBroken
 * on the server) and TestRun's reason; null — it loads. Such an account is
 * not given to tests.
 */
export const listBroken = (a: PoolAccountSummary): { detail: string } | null => {
  const cap = a.capabilities?.['accountsList'];
  if (cap) return cap.status === 'fail' ? { detail: cap.detail } : null;
  // TestRun's own words: older checks only said it in the readiness detail
  const item = a.readiness.find((x) => /не удалось загрузить: счета/.test(x.detail ?? ''));
  return item ? { detail: item.detail } : null;
};

/* ─── the pool's readiness columns ─── */

/** «Login» is not a column: everyone in the pool has logged in. */
export const POOL_COLUMNS = [
  'payments',
  'inBetween',
  'largeSum',
  'deposit',
  'salary',
  'loan',
  'cards',
  'ai',
] as const;
export type PoolColumn = (typeof POOL_COLUMNS)[number];

/* ─── registration ─── */

/** The registration classes of authenticate_api_testing, by kind. */
export const REG_CLASS: Record<RegistrationKind, string> = {
  IP: 'RegistrationIe',
  TOO: 'RegistrationLlp',
  MF: 'RegistrationLlpMultiFounder',
  SIMP: 'RegistrationSimplifiedAccess',
};
export const REG_KINDS = Object.keys(REG_CLASS) as RegistrationKind[];
export const REG_STATUSES: RegistrationStatus[] = [
  'free',
  'issued',
  'registered',
  'used',
  'unsuitable',
];

/* ─── import ─── */

export type ImportProblem = 'excelNumber' | 'phone' | 'iin' | 'bin' | 'kind' | 'companyBin';

export interface ImportParse<T> {
  rows: T[];
  bad: { line: number; text: string; reason: ImportProblem }[];
}

const cellsOf = (line: string) => line.split(/[;\t,]/).map((c) => c.trim());
/** Excel's text formula ="…" around a value. */
const unformula = (cell: string) => cell.replace(/^="?|"$/g, '');
/** Comment lines (#) and a header without digits are skipped. */
const isDataLine = (line: string) => Boolean(line) && !line.startsWith('#') && /\d/.test(line);

/** «phone; IIN; BIN; label» per line; «;», a tab or a comma between. The rest the server checks. */
export const parseImport = (text: string): ImportParse<ImportRow> => {
  const rows: ImportRow[] = [];
  const bad: ImportParse<ImportRow>['bad'] = [];
  text.split(/\r?\n/).forEach((raw, i) => {
    const line = raw.trim();
    if (!isDataLine(line)) return;
    const [phone = '', iin = '', bin = '', ...rest] = cellsOf(line);
    const row: ImportRow = {
      phone: normalizePhone(unformula(phone)),
      iin: digits12(unformula(iin)),
      line: i + 1,
    };
    const binDigits = digits12(unformula(bin));
    if (binDigits) row.bin = binDigits;
    const label = rest.join(', ').trim();
    if (label) row.label = label;
    const reason: ImportProblem | null = /e\+?\d/i.test(`${iin} ${bin}`)
      ? 'excelNumber'
      : !/^\+7\d{10}$/.test(row.phone)
        ? 'phone'
        : !/^\d{12}$/.test(row.iin)
          ? 'iin'
          : row.bin && !/^\d{12}$/.test(row.bin)
            ? 'bin'
            : null;
    if (reason) bad.push({ line: i + 1, text: line.slice(0, 60), reason });
    else rows.push(row);
  });
  return { rows, bad };
};

const REG_KIND_BY_CELL: Record<string, RegistrationKind> = {
  IP: 'IP',
  ИП: 'IP',
  TOO: 'TOO',
  ТОО: 'TOO',
  LLP: 'TOO',
  MF: 'MF',
  SIMP: 'SIMP',
};

/** «kind; phone; IIN; BIN; label»: kind — IP, TOO, MF or SIMP (or ИП / ТОО); a company needs a BIN. */
export const parseRegistrationImport = (text: string): ImportParse<RegistrationImportRow> => {
  const rows: RegistrationImportRow[] = [];
  const bad: ImportParse<RegistrationImportRow>['bad'] = [];
  text.split(/\r?\n/).forEach((raw, i) => {
    const line = raw.trim();
    if (!isDataLine(line)) return;
    const [k = '', phone = '', iin = '', bin = '', ...rest] = cellsOf(line);
    const kind = REG_KIND_BY_CELL[k.toUpperCase()];
    const parsedPhone = normalizePhone(unformula(phone));
    const parsedIin = digits12(unformula(iin));
    const parsedBin = digits12(unformula(bin));
    const reason: ImportProblem | null = !kind
      ? 'kind'
      : !/^\+7\d{10}$/.test(parsedPhone)
        ? 'phone'
        : !/^\d{12}$/.test(parsedIin)
          ? 'iin'
          : kind !== 'IP' && !/^\d{12}$/.test(parsedBin)
            ? 'companyBin'
            : null;
    if (reason || !kind) {
      bad.push({ line: i + 1, text: line.slice(0, 60), reason: reason ?? 'kind' });
      return;
    }
    const row: RegistrationImportRow = { kind, phone: parsedPhone, iin: parsedIin, line: i + 1 };
    if (parsedBin) row.bin = parsedBin;
    const label = rest.join(', ').trim();
    if (label) row.label = label;
    rows.push(row);
  });
  return { rows, bad };
};

/**
 * A template for Excel: «;» and a BOM so a Russian locale opens it by columns
 * and in UTF-8; IIN and BIN as ="…" formulas, or Excel turns them into
 * numbers and loses the leading zeros. The values are made up.
 */
export const importTemplate = (text: {
  comments: [string, string];
  header: string;
  labels: [string, string];
}): string =>
  [
    `# ${text.comments[0]}`,
    `# ${text.comments[1]}`,
    text.header,
    `="+70000000001";="000000000001";;${text.labels[0]}`,
    `="+70000000002";="000000000002";="000000000003";${text.labels[1]}`,
  ].join('\r\n');

/** Hands the template to the browser as a file; nothing leaves the page. */
export const saveTemplate = (content: string, fileName: string): void => {
  const blob = new Blob([`\uFEFF${content}\r\n`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

/* ─── numbers and dates ─── */

/**
 * Money, counts and the stand's dates in the interface language. The stand
 * gives dates in UTC (2026-09-20T19:00:00Z) — people read the day in Almaty.
 */
export const useAccountFormat = () => {
  const { locale, t } = useT();
  return useMemo(() => {
    const tag = INTL_LOCALES[locale];
    const amount = new Intl.NumberFormat(tag, { maximumFractionDigits: 2 });
    const whole = new Intl.NumberFormat(tag, { maximumFractionDigits: 0 });
    const almatyDay = new Intl.DateTimeFormat(tag, {
      timeZone: 'Asia/Almaty',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    const empty = t('common.empty');
    const sign = (currency: string | null | undefined) =>
      !currency || currency === 'KZT' ? '₸' : currency;

    return {
      money: (n: number | null | undefined, currency: string | null = 'KZT') =>
        n == null ? empty : `${amount.format(n)} ${sign(currency)}`,
      count: (n: number | null | undefined) => (n == null ? empty : String(n)),
      seconds: (ms: number) => t('automation.accounts.seconds', { value: (ms / 1000).toFixed(1) }),
      day: (iso: string | null) => (iso ? almatyDay.format(new Date(iso)) : null),
      /** «Current · KZ…1234 · 1 000 ₸» for account pickers */
      accountLabel: (a: StandAccountRow) =>
        `${a.name}${a.number ? ` ${a.number}` : ''} · ${
          a.balance == null ? empty : whole.format(Math.floor(a.balance))
        } ${sign(a.currency)}`,
    };
  }, [locale, t]);
};
